import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "./firebase";
import { api } from "./api";

export interface Profile {
  user: any | null;
  workspace: any | null;
  teams: any[];
}

interface AuthState {
  user: User | null;
  profile: Profile;
  loading: boolean;
  isDemo: boolean;
  refresh: () => Promise<void>;
}

const EMPTY_PROFILE: Profile = { user: null, workspace: null, teams: [] };

// ── localStorage profile cache ───────────────────────────────────────────────
// Caches the last-known profile keyed by UID so returning users see their
// workspace data instantly on page load instead of waiting for the Cloud
// Function cold-start (which can add 3–5 s on first hit after inactivity).

function profileKey(uid: string) { return `vt_profile_${uid}`; }

function readCached(uid: string): Profile | null {
  try {
    const raw = localStorage.getItem(profileKey(uid));
    return raw ? (JSON.parse(raw) as Profile) : null;
  } catch { return null; }
}

function writeCache(uid: string, p: Profile) {
  try { localStorage.setItem(profileKey(uid), JSON.stringify(p)); } catch { /* non-fatal */ }
}

function clearCache(uid: string) {
  try { localStorage.removeItem(profileKey(uid)); } catch {}
}

// ────────────────────────────────────────────────────────────────────────────

const Ctx = createContext<AuthState>({
  user: null,
  profile: EMPTY_PROFILE,
  loading: true,
  isDemo: false,
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  // Track the current UID in a ref so the sign-out branch can clear the cache
  // without relying on potentially-stale React state.
  const uidRef = useRef<string | null>(null);

  const refresh = async () => {
    if (!auth.currentUser) {
      setProfile(EMPTY_PROFILE);
      return;
    }
    try {
      const res = await api.getWorkspaceBootstrap();
      const fresh: Profile = { user: res.user, workspace: res.workspace, teams: res.teams };
      setProfile(fresh);
      writeCache(auth.currentUser.uid, fresh);
      // Best-effort one-off migration of legacy rule names. Safe to call
      // repeatedly; the server only updates docs whose name is in the rename map.
      if (res.user?.role === "workspace_admin") {
        api.migrateRuleNames().catch(() => {});
      }
    } catch (err: any) {
      // Self-heal: if the user is signed in via Auth but has no Firestore
      // profile (e.g. failed createWorkspace half-way), and their email matches
      // the demo account, attach them to the demo workspace automatically.
      const email = auth.currentUser.email ?? "";
      const looksLikeDemo = email.endsWith("@ventrin.test");
      if (looksLikeDemo && /not.found|failed-precondition/i.test(err?.message ?? "")) {
        try {
          await api.repairDemoProfile();
          const res = await api.getWorkspaceBootstrap();
          const fresh: Profile = { user: res.user, workspace: res.workspace, teams: res.teams };
          setProfile(fresh);
          writeCache(auth.currentUser.uid, fresh);
          return;
        } catch {}
      }
      setProfile(EMPTY_PROFILE);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        uidRef.current = u.uid;

        // Detect demo sessions. getDemoCredentials stamps a `demo: true`
        // custom claim onto the token. Fallback: email suffix.
        try {
          const tok = await u.getIdTokenResult();
          const demoClaim = tok.claims?.demo === true;
          const demoEmail = (u.email ?? "").endsWith("@ventrin.test");
          setIsDemo(Boolean(demoClaim || demoEmail));
        } catch {
          setIsDemo((u.email ?? "").endsWith("@ventrin.test"));
        }

        // Return visitor: hydrate from localStorage immediately so the UI
        // renders without waiting for the Cloud Function cold-start.
        const cached = readCached(u.uid);
        if (cached) {
          setProfile(cached);
          setLoading(false);    // show the app now — stale-while-revalidate
          refresh().catch(() => {}); // refresh silently in the background
        } else {
          // First visit: wait for the network call before showing the app.
          await refresh();
          setLoading(false);
        }
      } else {
        if (uidRef.current) clearCache(uidRef.current);
        uidRef.current = null;
        setProfile(EMPTY_PROFILE);
        setIsDemo(false);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  return <Ctx.Provider value={{ user, profile, loading, isDemo, refresh }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
